import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { isRemovedFromDashpivot } from "../lib/dashpivotRemoval";
import { logError } from "../lib/logError";
import { fetchAllRows } from "../lib/fetchAllRows";
import { buildContractAndSubcatMap, summariseClaimLines } from "./dashboardClaimsMath";

interface MonthlyBreakdown {
  month: number;
  monthName: string;
  amount: number;
}

/** Raw claims progress row for client-side filtering. */
export interface ClaimsProgressRow {
  amount: number;
  claimMonth: number;
  claimYear: number;
  subcategoryId: string;
}

interface DashboardStats {
  totalContractValue: number;
  activeProjects: number;
  pendingVariations: number;
  thisMonthClaims: number;
  lastMonthClaims: number;
  totalClaimsToDate: number;
  yearToDateClaims: number;
  monthlyBreakdown: MonthlyBreakdown[];
  totalApprovedVariations: number;
  /** Raw per-row claims data (includes subcategory_id for project mapping). */
  rawClaimsProgress: ClaimsProgressRow[];
  /** Subcategory ID → Project ID mapping for client-side filtering. */
  subcatToProject: Record<string, string>;
}

// select("*") rather than a column list, matching useVariationsStats (see the
// note where it is used). Paged by id cursor (see fetchAllRows).
function fetchVariationRows(projectIds: string[]) {
  return fetchAllRows((afterId, limit) => {
    let query = supabase
      .from("claims_variations")
      .select("*")
      .in("project_id", projectIds)
      .order("id", { ascending: true })
      .limit(limit);
    if (afterId) query = query.gt("id", afterId);
    return query;
  });
}

export function useDashboardStats() {
  const [stats, setStats] = useState<DashboardStats>({
    totalContractValue: 0,
    activeProjects: 0,
    pendingVariations: 0,
    thisMonthClaims: 0,
    lastMonthClaims: 0,
    totalClaimsToDate: 0,
    yearToDateClaims: 0,
    monthlyBreakdown: [],
    totalApprovedVariations: 0,
    rawClaimsProgress: [],
    subcatToProject: {},
  });
  const [loading, setLoading] = useState(true);
  // True when any read failed. The dashboard then shows "could not load" instead
  // of figures: a failed read must never pass as a real zero (SEQ-240).
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    // Set by the cleanup so a slow older read can never overwrite a newer one.
    let cancelled = false;
    async function fetchStats() {
      setLoading(true);
      setError(false);
      try {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        // Fetch all projects
        const { data: projects, error: projectsError } = await supabase
          .from("claims_projects")
          .select("id");
        if (projectsError) throw projectsError;

        const projectIds = (projects || []).map((p) => p.id);
        const activeProjects = projectIds.length;

        // Fetch all categories and subcategories to calculate contract value
        // 170 categories today; the embedded subcategories are not row-capped.
        let totalContractValue = 0;
        let subcatToProject: Record<string, string> = {};
        if (projectIds.length > 0) {
          const { data: categories, error: categoriesError } = await supabase
            .from("claims_categories")
            .select("*, subcategories:claims_subcategories(*)")
            .in("project_id", projectIds);
          if (categoriesError) throw categoriesError;

          ({ totalContractValue, subcatToProject } = buildContractAndSubcatMap(categories || []));
        }

        // Fetch pending variations (waiting_amount > 0) and approved variations
        // Credits SUBTRACT from total (to offset entries already in contract value)
        let pendingVariations = 0;
        let totalApprovedVariations = 0;
        if (projectIds.length > 0) {
          // select("*") rather than a column list, matching useVariationsStats.
          // Naming dashpivot_removed_at here coupled the whole dashboard to a
          // migration: one unknown column and PostgREST fails the request, the
          // forEach never runs, and every variation figure silently reads zero.
          // Paged: 754 variations today and growing, past the 1,000-row response
          // cap within months. A failed page throws to the catch at the bottom,
          // which sets the error state, so a clipped or missing list can never
          // read as zero money.
          const variations = await fetchVariationRows(projectIds);

          (variations || []).forEach((v) => {
            // A variation sync flagged as gone from Dashpivot may be a duplicate of
            // one now living under another project, so it must not be counted.
            if (isRemovedFromDashpivot(v)) return;
            // Only count 'valid' status (or null for backwards compatibility)
            if (v.status && v.status !== "valid") return;

            // Credits subtract, debits add
            const multiplier = v.debit_credit === "credit" ? -1 : 1;

            const rawTotal = Number(v.total || 0);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            const discount = Number((v as any).discount_percent) || 0;
            const effectiveTotal = rawTotal * (1 - discount / 100);
            const approved = Number(v.approved_amount || 0);

            // Calculate waiting dynamically to respect discount
            let waiting = effectiveTotal - approved;
            if (waiting < 0.01) waiting = 0;

            if (waiting > 0) {
              pendingVariations++;
            }
            totalApprovedVariations += approved * multiplier;
          });
        }

        // Claims progress for comprehensive stats. ALL lines, paged: the table
        // holds 1,494 rows and the API returns at most 1,000 per request, so an
        // unpaged select read two thirds of the money (SEQ-240). A failed page
        // throws to the catch below rather than yield a short total.
        let rawClaimsProgress: ClaimsProgressRow[] = [];
        let summary = summariseClaimLines([], { month: currentMonth, year: currentYear });

        if (projectIds.length > 0) {
          const progress = await fetchAllRows((afterId, limit) => {
            let query = supabase
              .from("claims_progress")
              .select("id, amount, claim_month, claim_year, subcategory_id")
              .order("id", { ascending: true })
              .limit(limit);
            if (afterId) query = query.gt("id", afterId);
            return query;
          });

          // Build raw claims progress array for client-side filtering
          rawClaimsProgress = progress.map((p) => ({
            amount: Number(p.amount || 0),
            claimMonth: p.claim_month,
            claimYear: p.claim_year,
            subcategoryId: p.subcategory_id,
          }));

          summary = summariseClaimLines(progress, { month: currentMonth, year: currentYear });
        }

        const {
          thisMonthClaims,
          lastMonthClaims,
          totalClaimsToDate,
          yearToDateClaims,
          monthlyBreakdown,
        } = summary;

        if (cancelled) return;
        setStats({
          totalContractValue,
          activeProjects,
          pendingVariations,
          thisMonthClaims,
          lastMonthClaims,
          totalClaimsToDate,
          yearToDateClaims,
          monthlyBreakdown,
          totalApprovedVariations,
          rawClaimsProgress,
          subcatToProject,
        });
      } catch (err) {
        logError("Error fetching dashboard stats:", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return { stats, loading, error, retry };
}
